import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { useLabels } from "@/lib/auth";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function AdminItems() {
  const qc = useQueryClient();
  const L = useLabels();
  const { data = [] } = useQuery({
    queryKey: ["items-all"],
    queryFn: async () => (await api.get("/items", { params: { include_inactive: true } })).data,
  });
  const empty = { sku: "", name: "", supplier_name: "", aliases: "", category: "essential_oil", base_unit: "kg", reorder_level: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/items", {
        ...form,
        supplier_name: form.supplier_name.trim() || null,
        aliases: form.aliases.split(",").map(s => s.trim()).filter(Boolean),
        reorder_level: form.reorder_level ? Number(form.reorder_level) : null,
      });
      toast.success("Item created");
      setForm(empty);
      qc.invalidateQueries({ queryKey: ["items-all"] });
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  const toggle = async (i) => {
    try {
      await api.patch(`/items/${i.id}`, { active: !i.active });
      qc.invalidateQueries({ queryKey: ["items-all"] });
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-items">
      <div>
        <div className="data-label">Master</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Items</h1>
      </div>
      <div className="bg-white border border-border rounded-sm p-4 grid md:grid-cols-4 gap-2 items-end">
        <input className="field-sm font-mono" placeholder="SKU" value={form.sku}
               onChange={(e) => setForm(f => ({ ...f, sku: e.target.value }))} data-testid="item-sku" />
        <input className="field-sm" placeholder="Our name (what buyers see)" value={form.name}
               onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="item-name" />
        <input className="field-sm" placeholder={`${L.tradeName} (optional)`} value={form.supplier_name}
               onChange={(e) => setForm(f => ({ ...f, supplier_name: e.target.value }))} data-testid="item-supplier-name" />
        <input className="field-sm" placeholder="Other names, comma-separated" value={form.aliases}
               onChange={(e) => setForm(f => ({ ...f, aliases: e.target.value }))} data-testid="item-aliases" />
        <select className="field-sm" value={form.category}
                onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} data-testid="item-category">
          <option value="essential_oil">essential_oil</option>
          <option value="aroma_chemical">aroma_chemical</option>
          <option value="fragrance">fragrance</option>
          <option value="butter">butter</option>
          <option value="carrier_oil">carrier_oil</option>
          <option value="packaging">packaging</option>
          <option value="finished_good">finished_good</option>
        </select>
        <select className="field-sm" value={form.base_unit}
                onChange={(e) => setForm(f => ({ ...f, base_unit: e.target.value }))} data-testid="item-unit">
          <option value="kg">kg</option>
          <option value="L">L</option>
          <option value="pcs">pcs</option>
        </select>
        <input className="field-sm font-mono" placeholder="Reorder lvl" type="number" step="0.001"
               value={form.reorder_level}
               onChange={(e) => setForm(f => ({ ...f, reorder_level: e.target.value }))} data-testid="item-reorder" />
        <button onClick={create} disabled={busy || !form.sku || !form.name} className="btn-primary"
                data-testid="item-create">
          <Plus className="w-4 h-4 mr-1" /> Add
        </button>
      </div>
      <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="items-list">
        {data.map(i => (
          <div key={i.id} className="p-3 flex items-center gap-3 flex-wrap">
            <span className="badge-flat bg-primary/10 text-primary">{i.category}</span>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{i.name}</div>
              <div className="container-code text-muted-foreground">
                {i.sku} · {i.base_unit}
                {i.supplier_name ? ` · ${L.tradeNameLower}: ${i.supplier_name}` : ""}
                {i.aliases?.length ? ` · aka ${i.aliases.join(", ")}` : ""}
              </div>
            </div>
            <div className="text-xs data-label">RE {i.reorder_level ?? "default"}</div>
            {!i.active && <span className="badge-flat bg-muted text-muted-foreground">disabled</span>}
            <button onClick={() => toggle(i)} className="btn-ghost text-xs"
                    data-testid={`item-toggle-${i.sku}`}>{i.active ? "Disable" : "Enable"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
